import { finalize } from 'rxjs/operators';
import { DialogPosition, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/overlay';

/**
 * Generic helper class that opens the specified dialog with the passed data and keeps it open at the current mouse cursor position.
 * The handleEvent method should be called on events like mouse enter, move, leave.
 */
export class OverlayDialogHelper<T, D extends { quoteId: string }> {
    private current: string;
    private currentDialogHeight = 0;
    private currentDialogWidth = 0;
    private dialogRef: MatDialogRef<T>;

    constructor(
        private document: Document,
        private dialog: MatDialog,
        private dialogType: ComponentType<T>,
        private panelClass: string
    ) {}

    public handleEvent(event: MouseEvent, obj: D) {
        switch (event.type) {
            case 'mouseleave': {
                this.current = null;
                this.currentDialogHeight = 0;
                this.currentDialogWidth = 0;
                if (this.dialogRef) {
                    this.dialogRef.close();
                }
                break;
            }
            case 'mouseenter': {
                this.mouseEnter(event, obj);
                break;
            }
            case 'mousemove': {
                if (this.dialogRef) {
                    this.dialogRef.updatePosition(this.getDialogPosition(event));
                }
                break;
            }
        }
    }

    private mouseEnter(event: MouseEvent, data: D) {
        if (this.dialogRef && this.current === data.quoteId) {
            return;
        }
        this.current = data.quoteId;
        this.dialogRef = this.dialog.open(this.dialogType, {
            hasBackdrop: false,
            width: '600px',
            panelClass: this.panelClass,
            position: this.getDialogPosition(event),
            data,
        });

        this.dialogRef.afterOpened().subscribe(() => {
            const dialog = this.document.getElementById(this.dialogRef.id);
            if (!dialog) {
                return;
            }
            const rect = dialog.getBoundingClientRect();
            this.currentDialogWidth = rect.width;
            this.currentDialogHeight = rect.height;
            this.dialogRef.updatePosition(this.getDialogPosition(event));
        });
        this.dialogRef.afterClosed().pipe(finalize(() => (this.dialogRef = undefined)));
    }

    private getDialogPosition(event: MouseEvent): DialogPosition {
        // show the dialog at the position of the cursor.
        // attach the upper left or bottom left corner of the dialog to the cursor, depending on where it fits on the screen.
        // Distance between dialog and viewport
        const margin = 8;
        // Distance between cursor and dialog
        const gap = 10;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const dialogWidth = this.currentDialogWidth || 600;
        const dialogHeight = this.currentDialogHeight || 0;
        let offsetLeft = event.clientX + gap;
        let offsetTop = event.clientY + gap;

        if (offsetLeft + dialogWidth + margin > viewportWidth) {
            offsetLeft = event.clientX - gap - dialogWidth;
        }
        if (offsetTop + dialogHeight + margin > viewportHeight) {
            offsetTop = event.clientY - gap - dialogHeight;
        }
        offsetLeft = Math.max(margin, Math.min(offsetLeft, viewportWidth - dialogWidth - margin));
        offsetTop = Math.max(margin, Math.min(offsetTop, viewportHeight - dialogHeight - margin));

        return {
            left: `${offsetLeft}px`,
            top: `${offsetTop}px`,
        };
    }

    public destroy() {
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }
}
